import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  User,
  Shield,
  Bell,
  BellRing,
  Mail,
  Languages,
  Palette,
  KeyRound,
  Smartphone,
  Copy,
  Check,
  Loader2,
  Save,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { apiFetch } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import type {
  UserProfile,
  NotificationPrefs,
  UpdateProfileRequest,
  UpdateProfileResponse,
  RegenerateRecoveryCodesResponse,
  ResetTotpResponse,
} from '@uniticket/shared';

// ── Notification preference keys ─────────────────────────────

const NOTIFICATION_KEYS: { emailKey: keyof NotificationPrefs; inappKey: keyof NotificationPrefs; labelKey: string; descKey: string }[] = [
  { emailKey: 'ticket_created', inappKey: 'inapp_ticket_created', labelKey: 'notifications.ticketCreated', descKey: 'notifications.ticketCreatedDesc' },
  { emailKey: 'ticket_assigned', inappKey: 'inapp_ticket_assigned', labelKey: 'notifications.ticketAssigned', descKey: 'notifications.ticketAssignedDesc' },
  { emailKey: 'comment_on_tickets', inappKey: 'inapp_comment_on_tickets', labelKey: 'notifications.newComments', descKey: 'notifications.newCommentsDesc' },
  { emailKey: 'status_change', inappKey: 'inapp_status_change', labelKey: 'notifications.statusChanges', descKey: 'notifications.statusChangesDesc' },
  { emailKey: 'sla_warning', inappKey: 'inapp_sla_warning', labelKey: 'notifications.slaWarnings', descKey: 'notifications.slaWarningsDesc' },
  { emailKey: 'sla_breach', inappKey: 'inapp_sla_breach', labelKey: 'notifications.slaBreaches', descKey: 'notifications.slaBreachesDesc' },
  { emailKey: 'escalation', inappKey: 'inapp_escalation', labelKey: 'notifications.escalations', descKey: 'notifications.escalationsDesc' },
  { emailKey: 'ticket_reopened', inappKey: 'inapp_ticket_reopened', labelKey: 'notifications.ticketReopened', descKey: 'notifications.ticketReopenedDesc' },
  { emailKey: 'mentioned', inappKey: 'inapp_mentioned', labelKey: 'notifications.mentioned', descKey: 'notifications.mentionedDesc' },
];

// ── Toggle Switch Component ──────────────────────────────────

function Toggle({ enabled, onToggle }: { enabled: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`relative w-10 h-5 rounded-full transition-colors ${enabled ? 'bg-brand-rose' : 'bg-slate-300'}`}
      role="switch"
      aria-checked={enabled}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform shadow-sm ${
          enabled ? 'translate-x-5' : ''
        }`}
      />
    </button>
  );
}

// ── Date formatting helper ───────────────────────────────────

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ── ProfilePage ──────────────────────────────────────────────

export function ProfilePage() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const { t, i18n } = useTranslation(['profile', 'common']);

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Personal info editing
  const [editingName, setEditingName] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [savingName, setSavingName] = useState(false);

  // Security dialogs
  const [regenDialogOpen, setRegenDialogOpen] = useState(false);
  const [resetTotpDialogOpen, setResetTotpDialogOpen] = useState(false);
  const [recoveryCodesDialogOpen, setRecoveryCodesDialogOpen] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [copiedCodes, setCopiedCodes] = useState(false);
  const [securityActionLoading, setSecurityActionLoading] = useState(false);

  // ── Fetch profile ────────────────────────────────────────

  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { user: profileData } = await apiFetch<{ user: UserProfile }>('/users/me/profile');
      setProfile(profileData);
      setDisplayName(profileData.displayName);
    } catch (err: any) {
      setError(err.message || t('loadError'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // ── Save profile helper ──────────────────────────────────

  async function saveProfile(data: UpdateProfileRequest) {
    try {
      const { user: updatedUser } = await apiFetch<UpdateProfileResponse>('/users/me', {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
      updateUser(updatedUser);
      // Update local profile state with new values
      setProfile((prev) =>
        prev
          ? {
              ...prev,
              ...updatedUser,
              // Preserve extended profile fields that aren't in AuthUser
              totpEnabled: prev.totpEnabled,
              recoveryCodeCount: prev.recoveryCodeCount,
              notificationPrefs: data.notificationPrefs ?? prev.notificationPrefs,
              passwordChangedAt: prev.passwordChangedAt,
              lastLoginAt: prev.lastLoginAt,
              createdAt: prev.createdAt,
            }
          : prev,
      );
      return true;
    } catch (err: any) {
      toast.error(err.message || t('security.saveError'));
      return false;
    }
  }

  // ── Name save handler ────────────────────────────────────

  async function handleSaveName() {
    const trimmed = displayName.trim();
    if (!trimmed) {
      toast.error(t('personalInfo.nameEmpty'));
      return;
    }
    if (trimmed === profile?.displayName) {
      setEditingName(false);
      return;
    }
    setSavingName(true);
    const ok = await saveProfile({ displayName: trimmed });
    setSavingName(false);
    if (ok) {
      toast.success(t('personalInfo.nameUpdated'));
      setEditingName(false);
    }
  }

  // ── Language / Theme change handlers ─────────────────────

  async function handleLanguageChange(value: string) {
    const ok = await saveProfile({ languagePref: value });
    if (ok) {
      i18n.changeLanguage(value);
      toast.success(t('langTheme.languageSaved'));
    }
  }

  async function handleThemeChange(value: string) {
    const ok = await saveProfile({ themePref: value });
    if (ok) toast.success(t('langTheme.themeSaved'));
  }

  // ── Notification toggle handler ──────────────────────────

  async function handleNotificationToggle(key: keyof NotificationPrefs) {
    if (!profile) return;
    const updated: NotificationPrefs = {
      ...profile.notificationPrefs,
      [key]: !profile.notificationPrefs[key],
    };
    const ok = await saveProfile({ notificationPrefs: updated });
    if (ok) toast.success(t('notifications.saved'));
  }

  // ── Regenerate recovery codes ────────────────────────────

  async function handleRegenerateCodes() {
    setSecurityActionLoading(true);
    try {
      const { recoveryCodes: codes } = await apiFetch<RegenerateRecoveryCodesResponse>(
        '/auth/regenerate-recovery-codes',
        { method: 'POST' },
      );
      setRecoveryCodes(codes);
      setRegenDialogOpen(false);
      setRecoveryCodesDialogOpen(true);
      setCopiedCodes(false);
      // Refresh profile to get updated recovery code count
      await fetchProfile();
      toast.success(t('security.regenSuccess'));
    } catch (err: any) {
      toast.error(err.message || t('security.regenError'));
    } finally {
      setSecurityActionLoading(false);
    }
  }

  // ── Reset TOTP ───────────────────────────────────────────

  async function handleResetTotp() {
    setSecurityActionLoading(true);
    try {
      await apiFetch<ResetTotpResponse>('/auth/reset-totp', { method: 'POST' });
      setResetTotpDialogOpen(false);
      toast.success(t('security.resetSuccess'));
      await fetchProfile();
    } catch (err: any) {
      toast.error(err.message || t('security.resetError'));
    } finally {
      setSecurityActionLoading(false);
    }
  }

  // ── Copy recovery codes to clipboard ─────────────────────

  async function handleCopyCodes() {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join('\n'));
      setCopiedCodes(true);
      toast.success(t('security.codeCopied'));
      setTimeout(() => setCopiedCodes(false), 3000);
    } catch {
      toast.error(t('security.copyError'));
    }
  }

  // ── Loading state ────────────────────────────────────────

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full rounded-xl" />
        <Skeleton className="h-40 w-full rounded-xl" />
        <Skeleton className="h-48 w-full rounded-xl" />
        <Skeleton className="h-56 w-full rounded-xl" />
      </div>
    );
  }

  // ── Error state ──────────────────────────────────────────

  if (error || !profile) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 text-center">
          <p className="text-slate-500 mb-4">{error || t('loadError')}</p>
          <Button onClick={fetchProfile} variant="outline">
            {t('common:actions.tryAgain')}
          </Button>
        </div>
      </div>
    );
  }

  const canManagePassword = user?.authType !== 'ENTRA_ID';

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-brand-gradient flex items-center justify-center">
          <User className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold gradient-text">{t('title')}</h1>
          <p className="text-sm text-slate-500">{t('subtitle')}</p>
        </div>
      </div>

      {/* ── Section 1: Personal Information ──────────────── */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
          <User className="w-4 h-4 text-brand-rose" />
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-700">{t('personalInfo.title')}</h2>
        </div>
        <div className="p-6 space-y-5">
          {/* Display Name (editable) */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 space-y-1.5">
              <Label className="text-xs text-slate-500 uppercase tracking-wider">{t('personalInfo.displayName')}</Label>
              {editingName ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="max-w-xs"
                    maxLength={100}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveName();
                      if (e.key === 'Escape') {
                        setDisplayName(profile.displayName);
                        setEditingName(false);
                      }
                    }}
                  />
                  <Button
                    size="sm"
                    onClick={handleSaveName}
                    disabled={savingName}
                    className="bg-brand-gradient text-white hover:opacity-90"
                  >
                    {savingName ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setDisplayName(profile.displayName);
                      setEditingName(false);
                    }}
                  >
                    {t('common:actions.cancel')}
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-slate-800">{profile.displayName}</p>
                  <button
                    onClick={() => setEditingName(true)}
                    className="text-xs text-brand-rose hover:underline font-medium"
                  >
                    {t('common:actions.edit')}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Read-only fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="space-y-1">
              <Label className="text-xs text-slate-500 uppercase tracking-wider">{t('personalInfo.email')}</Label>
              <p className="text-sm text-slate-800">{profile.email}</p>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-slate-500 uppercase tracking-wider">{t('personalInfo.role')}</Label>
              <p className="text-sm text-slate-800">{t(`common:role.${profile.role}`, { defaultValue: profile.role })}</p>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-slate-500 uppercase tracking-wider">{t('personalInfo.operator')}</Label>
              <p className="text-sm text-slate-800">
                {profile.operatorName}{' '}
                <span className="text-xs text-slate-400 font-mono">({profile.operatorCode})</span>
              </p>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-slate-500 uppercase tracking-wider">{t('personalInfo.authType')}</Label>
              <p className="text-sm text-slate-800">
                {profile.authType === 'ENTRA_ID'
                  ? t('personalInfo.authEntra')
                  : profile.authType === 'BOTH'
                    ? t('personalInfo.authBoth')
                    : t('personalInfo.authLocal')}
              </p>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-slate-500 uppercase tracking-wider">{t('personalInfo.accountCreated')}</Label>
              <p className="text-sm text-slate-800">{formatDate(profile.createdAt)}</p>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-slate-500 uppercase tracking-wider">{t('personalInfo.lastLogin')}</Label>
              <p className="text-sm text-slate-800">{formatDate(profile.lastLoginAt)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Section 2: Language & Theme ──────────────────── */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
          <Languages className="w-4 h-4 text-brand-rose" />
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-700">{t('langTheme.title')}</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Language */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Languages className="w-4 h-4 text-slate-400" />
                <Label>{t('langTheme.language')}</Label>
              </div>
              <Select value={profile.languagePref} onValueChange={handleLanguageChange}>
                <SelectTrigger>
                  <SelectValue placeholder={t('langTheme.selectLanguage')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">{t('langTheme.english')}</SelectItem>
                  <SelectItem value="nl">{t('langTheme.dutch')}</SelectItem>
                  <SelectItem value="fr">{t('langTheme.french')}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400">{t('langTheme.languageHint')}</p>
            </div>

            {/* Theme */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Palette className="w-4 h-4 text-slate-400" />
                <Label>{t('langTheme.theme')}</Label>
              </div>
              <Select value={profile.themePref} onValueChange={handleThemeChange}>
                <SelectTrigger>
                  <SelectValue placeholder={t('langTheme.selectTheme')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="system">{t('langTheme.system')}</SelectItem>
                  <SelectItem value="light">{t('langTheme.light')}</SelectItem>
                  <SelectItem value="dark">{t('langTheme.dark')}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400">{t('langTheme.themeHint')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Section 3: Notification Preferences ─────────── */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
          <Bell className="w-4 h-4 text-brand-rose" />
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-700">{t('notifications.title')}</h2>
        </div>
        {/* Column headers */}
        <div className="px-6 py-3 bg-slate-50 border-b border-slate-100 flex items-center">
          <div className="flex-1" />
          <div className="flex items-center gap-8">
            <div className="w-16 flex flex-col items-center gap-1">
              <BellRing className="w-3.5 h-3.5 text-slate-500" />
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{t('notifications.inApp')}</span>
            </div>
            <div className="w-16 flex flex-col items-center gap-1">
              <Mail className="w-3.5 h-3.5 text-slate-500" />
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{t('notifications.email')}</span>
            </div>
          </div>
        </div>
        <div className="divide-y divide-slate-100">
          {NOTIFICATION_KEYS.map((item) => (
            <div key={item.emailKey} className="px-6 py-4 flex items-center">
              <div className="flex-1 space-y-0.5 min-w-0">
                <p className="text-sm font-medium text-slate-800">{t(item.labelKey)}</p>
                <p className="text-xs text-slate-400">{t(item.descKey)}</p>
              </div>
              <div className="flex items-center gap-8 shrink-0">
                <div className="w-16 flex justify-center">
                  <Toggle
                    enabled={profile.notificationPrefs[item.inappKey]}
                    onToggle={() => handleNotificationToggle(item.inappKey)}
                  />
                </div>
                <div className="w-16 flex justify-center">
                  <Toggle
                    enabled={profile.notificationPrefs[item.emailKey]}
                    onToggle={() => handleNotificationToggle(item.emailKey)}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="px-6 py-3 bg-slate-50 border-t border-slate-100">
          <p className="text-[11px] text-slate-400">
            {t('notifications.hint')}
          </p>
        </div>
      </div>

      {/* ── Section 4: Security (LOCAL users only) ──────── */}
      {canManagePassword && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
            <Shield className="w-4 h-4 text-brand-rose" />
            <h2 className="text-sm font-bold uppercase tracking-wide text-slate-700">{t('security.title')}</h2>
          </div>
          <div className="p-6 space-y-6">
            {/* Change Password */}
            <div className="flex items-center justify-between">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center mt-0.5">
                  <KeyRound className="w-4 h-4 text-slate-500" />
                </div>
                <div className="space-y-0.5">
                  <p className="text-sm font-medium text-slate-800">{t('security.password')}</p>
                  <p className="text-xs text-slate-400">
                    {profile.passwordChangedAt
                      ? t('security.lastChanged', { date: formatDate(profile.passwordChangedAt) })
                      : t('security.neverChanged')}
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate('/profile/change-password')}>
                {t('security.changePassword')}
              </Button>
            </div>

            {/* TOTP Status */}
            <div className="flex items-center justify-between">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center mt-0.5">
                  <Smartphone className="w-4 h-4 text-slate-500" />
                </div>
                <div className="space-y-0.5">
                  <p className="text-sm font-medium text-slate-800">{t('security.totp')}</p>
                  <p className="text-xs text-slate-400">
                    {profile.totpEnabled ? (
                      <>
                        <span className="inline-flex items-center gap-1 text-green-600 font-semibold">{t('security.totpEnabled')}</span>
                        {' -- '}
                        {t('security.recoveryCodesRemaining', { count: profile.recoveryCodeCount })}
                      </>
                    ) : (
                      <span className="text-amber-600 font-semibold">{t('security.totpNotConfigured')}</span>
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Security action buttons */}
            {profile.totpEnabled && (
              <div className="flex flex-wrap gap-3 pl-12">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRegenDialogOpen(true)}
                >
                  {t('security.regenCodes')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                  onClick={() => setResetTotpDialogOpen(true)}
                >
                  {t('security.resetAuth')}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Confirmation Dialog: Regenerate Recovery Codes ─ */}
      <Dialog open={regenDialogOpen} onOpenChange={setRegenDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('security.regenTitle')}</DialogTitle>
            <DialogDescription>
              {t('security.regenDesc')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegenDialogOpen(false)} disabled={securityActionLoading}>
              {t('common:actions.cancel')}
            </Button>
            <Button
              onClick={handleRegenerateCodes}
              disabled={securityActionLoading}
              className="bg-brand-gradient text-white hover:opacity-90"
            >
              {securityActionLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('security.generating')}
                </>
              ) : (
                t('security.regenBtn')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Dialog: Show Recovery Codes ────────────────────── */}
      <Dialog open={recoveryCodesDialogOpen} onOpenChange={setRecoveryCodesDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('security.newCodesTitle')}</DialogTitle>
            <DialogDescription>
              {t('security.newCodesDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="bg-slate-50 rounded-lg p-4 font-mono text-sm space-y-1.5">
            {recoveryCodes.map((code, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-xs text-slate-400 w-5 text-right">{i + 1}.</span>
                <span className="text-slate-800 tracking-wider">{code}</span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCopyCodes} className="gap-2">
              {copiedCodes ? (
                <>
                  <Check className="w-4 h-4 text-green-600" />
                  {t('security.copied')}
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  {t('security.copyAll')}
                </>
              )}
            </Button>
            <Button onClick={() => setRecoveryCodesDialogOpen(false)}>{t('common:actions.done')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Confirmation Dialog: Reset TOTP ────────────────── */}
      <Dialog open={resetTotpDialogOpen} onOpenChange={setResetTotpDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('security.resetTitle')}</DialogTitle>
            <DialogDescription>
              {t('security.resetDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
            {t('security.resetWarning')}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetTotpDialogOpen(false)} disabled={securityActionLoading}>
              {t('common:actions.cancel')}
            </Button>
            <Button
              onClick={handleResetTotp}
              disabled={securityActionLoading}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {securityActionLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('security.resetting')}
                </>
              ) : (
                t('security.resetAuth')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
